import type { Fetcher } from '../../../src/http';
import { sf311CaseSourceUrl } from '../../../src/presentation';
import type { Sf311Case, Sf311Snapshot } from '../../../src/types';

export type Open311Request = {
  service_request_id?: string;
  description?: string;
  attributes?: Array<{ label?: string; value?: unknown; name?: string }>;
};

export type Sf311Row = {
  service_request_id?: string;
  requested_datetime?: string;
  closed_date?: string;
  updated_datetime?: string;
  status_description?: string;
  status_notes?: string;
  agency_responsible?: string;
  service_name?: string;
  service_subtype?: string;
  service_details?: string;
  address?: string;
  street?: string;
  lat?: string;
  long?: string;
  media_url?: string | { url?: string };
  source?: string;
};

export function createSf311Source(fetch: Fetcher) {
  const DATASET_API = 'https://data.sf.gov/resource/vw6y-z8j6.json';
  const DATASET_PAGE = 'https://data.sf.gov/City-Infrastructure/311-Cases/vw6y-z8j6';
  const OPEN311_API = 'https://mobile311.sfgov.org/open311/v2/requests.json';
  const MAX_CASES = 5_000;
  const REQUEST_ATTEMPTS = 3;

  function clean(value: string | null | undefined) {
    return (value ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function retryDelay(attempt: number) {
    return new Promise((resolve) => setTimeout(resolve, 150 * 2 ** attempt));
  }

  function dateValue(value: string) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function mediaUrl(value: Sf311Row['media_url']) {
    if (typeof value === 'string') return clean(value);
    return clean(value?.url);
  }

  function incidentUrl(id: string) {
    return sf311CaseSourceUrl(id);
  }

  function parseSf311Rows(rows: Sf311Row[], expectedCount: number): Sf311Case[] {
    if (!Number.isInteger(expectedCount) || expectedCount < 0 || expectedCount > MAX_CASES) {
      throw new Error('SF311 returned an invalid or unexpectedly large result count.');
    }
    if (!Array.isArray(rows) || rows.length !== expectedCount) {
      throw new Error('SF311 returned an incomplete case list.');
    }

    const cases = rows.map((row) => {
      const id = clean(row.service_request_id);
      const openedAt = clean(row.requested_datetime);
      const updatedAt = clean(row.updated_datetime);
      const status = clean(row.status_description);
      const address = clean(row.address);
      if (!/^\d+$/.test(id) || !openedAt || !updatedAt || !status || !address) {
        throw new Error('SF311 returned an incomplete case record.');
      }

      const fields = {
        Status: status,
        'Status notes': clean(row.status_notes),
        'Responsible agency': clean(row.agency_responsible),
        Category: clean(row.service_name),
        'Request type': clean(row.service_subtype),
        'Request details': clean(row.service_details),
        Address: address,
        Street: clean(row.street),
        Opened: openedAt,
        Updated: updatedAt,
        Closed: clean(row.closed_date),
        Source: clean(row.source),
        Latitude: clean(row.lat),
        Longitude: clean(row.long),
        'Media URL': mediaUrl(row.media_url),
      };

      return {
        id,
        openedAt,
        updatedAt,
        closedAt: fields.Closed,
        status,
        statusNotes: fields['Status notes'],
        agency: fields['Responsible agency'],
        category: fields.Category,
        subtype: fields['Request type'],
        details: fields['Request details'],
        address,
        mediaUrl: fields['Media URL'],
        sourceUrl: incidentUrl(id),
        fields,
      } satisfies Sf311Case;
    });

    if (new Set(cases.map((incident) => incident.id)).size !== expectedCount) {
      throw new Error('SF311 returned duplicate or incomplete case records.');
    }
    return cases.sort((a, b) => dateValue(b.updatedAt) - dateValue(a.updatedAt));
  }

  function open311Detail(request: Open311Request) {
    const attributes: Record<string, string> = {};
    for (const attribute of request.attributes ?? []) {
      const label = clean(attribute.label).replace(/:+\s*$/, '');
      if (!label || attribute.value === null || attribute.value === undefined) continue;
      const value = clean(String(attribute.value));
      if (value) attributes[label] = value;
    }
    return { description: clean(request.description), attributes };
  }

  /**
   * DataSF omits the reporter's description and per-request form answers;
   * the Open311 API publishes them under `extensions=true`. Enrichment is
   * best-effort: a partial or failed read still returns the dataset cases.
   */
  async function fetchOpen311Details(
    ids: string[],
  ): Promise<Map<string, { description: string; attributes: Record<string, string> }>> {
    const found = new Map<string, { description: string; attributes: Record<string, string> }>();
    const requested = new Set(ids);
    for (let index = 0; index < ids.length; index += 100) {
      let requests: Open311Request[];
      try {
        const url = new URL(OPEN311_API);
        url.searchParams.set('service_request_id', ids.slice(index, index + 100).join(','));
        url.searchParams.set('per_page', '100');
        url.searchParams.set('extensions', 'true');
        requests = (await requestRows(url)) as Open311Request[];
      } catch {
        break;
      }
      if (!Array.isArray(requests)) break;
      for (const request of requests) {
        const id = clean(request.service_request_id);
        if (!requested.has(id)) continue;
        found.set(id, open311Detail(request));
      }
      if (index + 100 < ids.length) await retryDelay(0);
    }
    return found;
  }

  function sf311AddressPrefixes(addresses: string[]) {
    return [
      ...new Set(
        addresses.flatMap((address) => {
          const normalized = clean(address)
            .toUpperCase()
            .replace(/\s+#.*$/, '')
            .replace(/,.*$/, '')
            .replace(/[^A-Z0-9 -]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          const match = normalized.match(/^(\d+[A-Z]?)(?:\s*-\s*(\d+[A-Z]?))?\s+(.+)$/);
          if (!match) return [];
          const individual = [match[1], match[2]]
            .filter((number): number is string => Boolean(number))
            .map((number) => `${number} ${match[3]}`);
          const range = match[2] ? [`${match[1]}-${match[2]} ${match[3]}`] : [];
          return [...individual, ...range];
        }),
      ),
    ].sort();
  }

  async function requestRows(url: URL): Promise<Sf311Row[]> {
    let lastError: unknown;
    for (let attempt = 0; attempt < REQUEST_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetch(url, {
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'User-Agent': 'ParcelWatch/0.1 (+https://parcel-watch.schwartzdev.workers.dev)',
          },
        });
        if (!response.ok) throw new Error(`SF311 returned ${response.status}.`);
        const rows = await response.json();
        if (!Array.isArray(rows)) throw new Error('SF311 returned an invalid response.');
        return rows as Sf311Row[];
      } catch (error) {
        lastError = error;
        if (attempt < REQUEST_ATTEMPTS - 1) await retryDelay(attempt);
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error('SF311 could not be reached after retries.');
  }

  async function fetchSf311SnapshotUncached(addresses: string[]): Promise<Sf311Snapshot> {
    const prefixes = sf311AddressPrefixes(addresses);
    if (!prefixes.length) throw new Error('No parcel address could be used for the SF311 search.');
    const where = prefixes
      .map((prefix) => `upper(address) like '${prefix.replaceAll("'", "''")}%'`)
      .join(' or ');

    for (let attempt = 0; attempt < REQUEST_ATTEMPTS; attempt += 1) {
      const countUrl = new URL(DATASET_API);
      countUrl.searchParams.set('$select', 'count(*) as count');
      countUrl.searchParams.set('$where', where);
      const countRows = await requestRows(countUrl);
      const expectedCount = Number((countRows[0] as { count?: string } | undefined)?.count);
      if (!Number.isInteger(expectedCount) || expectedCount < 0 || expectedCount > MAX_CASES) {
        throw new Error('SF311 returned an invalid or unexpectedly large result count.');
      }

      const dataUrl = new URL(DATASET_API);
      dataUrl.searchParams.set(
        '$select',
        [
          'service_request_id',
          'requested_datetime',
          'closed_date',
          'updated_datetime',
          'status_description',
          'status_notes',
          'agency_responsible',
          'service_name',
          'service_subtype',
          'service_details',
          'address',
          'street',
          'lat',
          'long',
          'media_url',
          'source',
        ].join(','),
      );
      dataUrl.searchParams.set('$where', where);
      dataUrl.searchParams.set('$order', 'updated_datetime desc, service_request_id desc');
      dataUrl.searchParams.set('$limit', String(MAX_CASES));
      const rows = await requestRows(dataUrl);
      try {
        const cases = parseSf311Rows(rows, expectedCount);
        const details = await fetchOpen311Details(cases.map((ticket) => ticket.id));
        return {
          sourceUrl: DATASET_PAGE,
          resultCount: cases.length,
          cases: cases.map((ticket) => {
            const detail = details.get(ticket.id);
            return detail
              ? {
                  ...ticket,
                  description: detail.description,
                  attributes: detail.attributes,
                }
              : ticket;
          }),
        };
      } catch (error) {
        if (attempt === REQUEST_ATTEMPTS - 1) throw error;
        await retryDelay(attempt);
      }
    }

    throw new Error('SF311 did not return a complete case list after retries.');
  }

  function fetchSf311Snapshot(
    block: string,
    lot: string,
    addresses: string[],
  ): Promise<Sf311Snapshot> {
    return fetchSf311SnapshotUncached(addresses);
  }

  return {
    parseSf311Rows,
    sf311AddressPrefixes,
    fetchSf311Snapshot,
    open311Detail,
  };
}

export const { parseSf311Rows, sf311AddressPrefixes, open311Detail } = createSf311Source(
  (...args) => globalThis.fetch(...args),
);
